import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/pages/home/components/MovieCard';
import { getMoviesByCategory } from '@/mocks/movies';
import { categories, slugToCategory } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');

  const categoryName = slugToCategory[slug || ''] || '未知分类';
  const categoryInfo = categories.find((c) => c.slug === slug);
  const categoryMovies = getMoviesByCategory(categoryName);

  const sortedMovies = [...categoryMovies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'year') return b.year - a.year;
    return 0;
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 天狼影院`,
    description: categoryInfo?.description || `天狼影院${categoryName}频道`,
    url: `https://www.tianlangcinema.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '天狼影院',
      url: 'https://www.tianlangcinema.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-600">
                  {categoryInfo?.description}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                <div className="flex items-center bg-background-100 rounded-full p-1 gap-1">
                  {[
                    { key: 'default', label: '默认' },
                    { key: 'rating', label: '评分' },
                    { key: 'year', label: '年份' },
                  ].map((option) => (
                    <button
                      key={option.key}
                      onClick={() => setSortBy(option.key as typeof sortBy)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === option.key
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {sortedMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-14">
                {sortedMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-500">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容，请查看其他分类</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 mt-4 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                其他分类
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-primary-400/40 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 flex-shrink-0">
                        <i className={cat.icon}></i>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-foreground-900">{cat.name}</span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}